import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import type { PersistedUserFlow, PersistedUserFlowState, UserFlow } from "../types";
import { type ApiMutationOptions, useApiMutation, type WithDynamicUrl } from "./use-api-mutation";
import { useApiQuery } from "./use-api-query";

interface UserFlowCreateSchema {
  slug: string;
  depends_on?: string;
  name: string;
  description: string;
  steps: UserFlow["steps"];
  is_positive: boolean;
  success_criteria: string;
  need_user_input: boolean;
  user_input_question?: string;
  reasoning: string;
  priority: UserFlow["priority"];
  severity: UserFlow["severity"];
  confidence: UserFlow["confidence"];
}

interface UserFlowUpdateSchema {
  slug?: string;
  depends_on?: string;
  name?: string;
  description?: string;
  state?: PersistedUserFlowState;
  steps?: UserFlow["steps"];
  is_positive?: boolean;
  success_criteria?: string;
  need_user_input?: boolean;
  user_input_question?: string;
  reasoning?: string;
  priority?: UserFlow["priority"];
  severity?: UserFlow["severity"];
  confidence?: UserFlow["confidence"];
}

export const useFlows = () => {
  return useApiQuery<PersistedUserFlow[]>("/flows");
};

// New hook that supports search parameters
export const useFlowsWithSearch = (searchQuery?: string) => {
  const query = searchQuery ? `/flows?q=${encodeURIComponent(searchQuery)}` : "/flows";
  return useApiQuery<PersistedUserFlow[]>(query);
};

export const useFlow = (id: string) => {
  return useApiQuery<PersistedUserFlow>(`/flows/${id}`, {
    enabled: !!id,
  });
};

export const useCreateFlow = (
  opts?: ApiMutationOptions<UserFlowCreateSchema, PersistedUserFlow>,
) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useApiMutation<UserFlowCreateSchema, PersistedUserFlow>("POST", "/flows", {
    ...opts,
    onSuccess: (flow, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["/flows"] });

      enqueueSnackbar(`Flow '${flow.name}' created successfully`, {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      opts?.onSuccess?.(flow, variables, context);
    },
    onError: (error, variables, context) => {
      enqueueSnackbar(`Failed to create flow: ${error}`, {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      opts?.onError?.(error, variables, context);
    },
  });
};

export const useUpdateFlow = (id: string) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useApiMutation<UserFlowUpdateSchema, PersistedUserFlow>("PUT", `/flows/${id}`, {
    onSuccess: (flow) => {
      queryClient.invalidateQueries({ queryKey: [`/flows/${flow.id}`] });

      enqueueSnackbar(`Flow '${flow.name}' updated successfully`, {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to update flow: ${error}`, {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });
};

export const useDeleteFlow = (opts?: ApiMutationOptions<WithDynamicUrl<void>, void>) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useApiMutation<WithDynamicUrl<void>, void>("DELETE", "", {
    ...opts,
    onSuccess: (data, vars, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["/flows"] });

      enqueueSnackbar(`Flow deleted successfully`, {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      opts?.onSuccess?.(data, vars, ctx);
    },
    onError: (error, vars, ctx) => {
      enqueueSnackbar(`Failed to delete flow: ${error}`, {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      opts?.onError?.(error, vars, ctx);
    },
  });
};
